import React from 'react';
import { useTranslation } from 'next-i18next';

import ArticlePostModel from 'src/types/article/ArticlePostModel';
import Image from 'src/shared/components/Image';
import BaseText from 'src/shared/components/BaseText';
import Link from 'src/shared/components/Link';

import styles from './styles.module.scss';
import useArticleInfo from 'src/hooks/useArticleInfo';
import RoutePaths from 'src/config/RoutePaths';
import { fireGA } from 'src/utils/gaUtils';

export interface NewsPreviewProps {
    item: ArticlePostModel | null;
    type: 'row_left-img' | 'row_right-img' | 'column_wide' | 'column_normal' | string;
    className?: string;
    shouldShowLabel?: boolean;
    shouldShowFeaturedOverCategory?: boolean;
}

interface DetailFooterProps {
    item: ArticlePostModel;
}

const DetailsFooter = ({ item }: DetailFooterProps) => {
    const { infoText } = useArticleInfo(item);

    return (
        <div className={styles.footer} data-test="details-footer">
            <div className={styles.footerText}>{infoText}</div>
        </div>
    );
};
DetailsFooter.displayName = 'DetailsFooter';

const NewsPreview: React.FC<NewsPreviewProps> = (props) => {
    const { item } = props;
    const { t } = useTranslation();
    const { categoryLabel } = useArticleInfo(item || {});

    if (!item) {
        return null;
    }

    const [direction, mode] = props.type.split('_');
    const featuredLabel = t('article_featured');
    const label = props.shouldShowFeaturedOverCategory && item.featured ? featuredLabel : categoryLabel;

    const clickButton = () => {
        fireGA('marketingQ421', `click${item.category.categoryName}Article`, item.title);
    };

    return (
        <Link href={RoutePaths.NEWS_DETAIL} query={{ category: item.category ? item.category.categoryUrl : '', articleUrl: item.articleUrl }}>
            <button className={`${styles.container} ${styles[direction]} ${styles[mode]} ${props.className}`} onClick={clickButton}>
                <div className={styles.imageContainer}>
                    <Image
                        src={item.coverImage}
                        className={styles.image}
                        alt={item.coverImageAltTag}
                        dataTest="coverImage"
                        renderEmptyImg={() => (
                            <div className={styles.empty_image}>
                                <img className={styles.icon} src="/static/images/svg/fund_default_img.svg" />
                            </div>
                        )}
                    />
                </div>
                <div className={`${styles.content} ${props.shouldShowLabel ? styles.withLabel : ''}`}>
                    {props.shouldShowLabel && (
                        <BaseText className={styles.label} dataTest="label">
                            {label}
                        </BaseText>
                    )}
                    <BaseText className={styles.title} dataTest="title">
                        {item.title}
                    </BaseText>
                    {mode === 'wide' && (
                        <BaseText className={styles.excerpt} dataTest="excerpt">
                            {item.excerpt}
                        </BaseText>
                    )}
                    <DetailsFooter item={item} />
                </div>
            </button>
        </Link>
    );
};
NewsPreview.displayName = 'NewsPreview';

NewsPreview.defaultProps = {
    className: '',
    shouldShowLabel: true,
    shouldShowFeaturedOverCategory: false,
};

export default NewsPreview;
